import React from 'react';
import PropTypes from 'prop-types';
import { X } from 'lucide-react';
import styles from './BulkActionsBar.module.scss';

/* ────────────────────────────────────────────────────────────
 * BulkActionsBar — selection-aware action strip.
 *
 * Shown above a list when ≥ 1 rows are selected. Slides in
 * from below, replaces the toolbar's right slot, persists
 * until selection clears.
 *
 *   <BulkActionsBar
 *     count={selected.length}
 *     onClear={() => setSelected([])}
 *     actions={
 *       <>
 *         <Button variant="ghost" onClick={onExport}>Export</Button>
 *         <Button variant="danger" onClick={onDelete}>Delete</Button>
 *       </>
 *     }
 *   />
 *
 * Motion is the same family as StickyActionBar (MOTION.md §4.2):
 * slide-up + fade on enter.
 * ──────────────────────────────────────────────────────────── */

const BulkActionsBar = ({
  count,
  label,
  pluralLabel,
  actions,
  onClear,
  position,
  className,
}) => {
  if (!count) return null;

  const noun = count === 1 ? label : pluralLabel || `${label}s`;

  const classNames = [
    styles.bar,
    position === 'floating' ? styles.floating : styles.docked,
    className || '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classNames} role="region" aria-label={`${count} ${noun} selected`}>
      <div className={styles.left}>
        <span className={styles.badge}>{count}</span>
        <span className={styles.label}>{noun} selected</span>
        {onClear && (
          <button
            type="button"
            className={styles.clear}
            onClick={onClear}
            aria-label="Clear selection"
          >
            <X size={14} strokeWidth={1.75} />
            Clear
          </button>
        )}
      </div>
      <div className={styles.actions}>{actions}</div>
    </div>
  );
};

BulkActionsBar.propTypes = {
  count: PropTypes.number,
  label: PropTypes.string,
  pluralLabel: PropTypes.string,
  actions: PropTypes.node,
  onClear: PropTypes.func,
  position: PropTypes.oneOf(['docked', 'floating']),
  className: PropTypes.string,
};

BulkActionsBar.defaultProps = {
  count: 0,
  label: 'item',
  pluralLabel: '',
  actions: null,
  onClear: null,
  position: 'docked',
  className: '',
};

export default BulkActionsBar;
